"use client";

import Image from "next/image";

import { OutboundLink } from "@/features/outbound-link/outbound-link";
import { withBasePath } from "@/lib/base-path";
import { BiEvent } from "@/lib/bi/bi.enum";

import { usePrimeDayCountdown } from "../hooks/use-prime-day-countdown";
import {
  PopupCountdownDigit,
  PopupCountdownSeparator,
} from "./popup-countdown-digit";
import { SaleStrip } from "./sale-strip";

type Props = {
  productUrl: string;
  productName: string;
  productImage: string;
  onClickOut: () => void;
};

export function PopupMobile({
  productUrl,
  productName,
  productImage,
  onClickOut,
}: Props) {
  const { days, hours, minutes, seconds } = usePrimeDayCountdown();

  return (
    <OutboundLink
      url={productUrl}
      storeName="Amazon"
      bi={{
        eventType: BiEvent.BannerOutbound,
        provider: "amazon",
        location: "prime-day-popup",
      }}
      target="_blank"
      rel="noopener noreferrer"
      className="relative flex flex-col items-center rounded-2xl overflow-hidden w-[300px] cursor-pointer bg-grayscale-white"
      onClick={onClickOut}
    >
      {/* Top section — product image */}
      <div className="relative flex items-center justify-center rounded-t-2xl px-4 py-6 w-full">
        <Image
          src={productImage}
          alt={productName}
          width={140}
          height={140}
          className="object-contain"
        />
      </div>

      {/* Sale strip */}
      <SaleStrip className="w-full h-6" />

      {/* Bottom section — gradient */}
      <div className="flex flex-col items-center gap-5 bg-gradient-to-t from-[#fafdff] from-50% to-[#ddf1ff] rounded-b-2xl px-7 py-6 w-full">
        {/* Title + product name */}
        <div className="flex flex-col gap-2 items-start w-full">
          <p className="text-4xl font-bold leading-9 text-[#2c384a]">
            Prime Day
          </p>
          <p className="text-sm text-[#2c384a] w-full truncate">
            {productName}
          </p>
        </div>

        {/* Countdown section */}
        <div className="relative flex flex-col gap-1 w-full">
          {/* Bags SVG */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={withBasePath("/prime-day/bags.svg")}
            alt=""
            width={51}
            height={58}
            className="absolute bottom-[57px] -right-1"
          />
          <p className="text-base text-[#626e79]">Deal ends in</p>
          <div className="flex items-start gap-1">
            <PopupCountdownDigit
              value={days}
              label="Days"
              size="mobile"
            />
            <PopupCountdownSeparator size="mobile" />
            <PopupCountdownDigit
              value={hours}
              label="Hours"
              size="mobile"
            />
            <PopupCountdownSeparator size="mobile" />
            <PopupCountdownDigit
              value={minutes}
              label="Min"
              size="mobile"
            />
            <PopupCountdownSeparator size="mobile" />
            <PopupCountdownDigit
              value={seconds}
              label="Sec"
              size="mobile"
            />
          </div>
        </div>

        {/* CTA + Amazon */}
        <div className="flex flex-col items-center gap-3 w-full">
          <button className="w-full rounded-xl bg-cta px-5 py-2.5 text-base font-semibold text-grayscale-white">
            Check Prime day price
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={withBasePath("/logos/amazon.png")}
            alt="Amazon"
            width={51}
            height={16}
          />
        </div>
      </div>
    </OutboundLink>
  );
}
